/* src/components/Sidebar.jsx
 * gocta Operator Console sidebar — Paper theme reskin
 * Logic (lead access gating, admin gating, workspace switcher, mobile behavior) unchanged.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { canAccessLeads } from '../utils/accessControl';
import { isAdmin } from '../utils/admin';
import { SMS_ONBOARDING_LAUNCHED } from '../utils/flags';
import { useIsMobile } from '../hooks/useIsMobile';
import WorkspaceMenu from './WorkspaceMenu';

export default function Sidebar({ mobileOpen = false, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { currentUser, userProfile, logout } = useAuth();
  const { selectedWorkspaceId, isOwner } = useWorkspace();
  const [canAccessLeadsPage, setCanAccessLeadsPage] = useState(true);
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  useEffect(() => {
    if (currentUser) {
      checkLeadAccess();
      checkAdminAccess();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userProfile, selectedWorkspaceId]);

  async function checkLeadAccess() {
    if (!currentUser || !selectedWorkspaceId) return;
    const hasAccess = await canAccessLeads(currentUser.uid, selectedWorkspaceId);
    setCanAccessLeadsPage(hasAccess);
  }

  function checkAdminAccess() {
    if (!currentUser) return;
    setIsUserAdmin(isAdmin(currentUser, userProfile));
  }

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    if (path === '/form-builder') {
      return location.pathname.startsWith('/forms/') || location.pathname === '/form-builder';
    }
    if (path === '/leads') {
      return location.pathname.startsWith('/leads');
    }
    if (path === '/pipeline') {
      return location.pathname === '/pipeline';
    }
    if (path === '/custom-domains') {
      return location.pathname === '/custom-domains';
    }
    if (path === '/chat') {
      return location.pathname === '/chat';
    }
    if (path === '/knowledge-base') {
      return location.pathname === '/knowledge-base';
    }
    if (path === '/billing') {
      return location.pathname === '/billing';
    }
    if (path === '/text-messaging') {
      return location.pathname === '/text-messaging';
    }
    if (path === '/advanced-settings') {
      return location.pathname === '/advanced-settings' || location.pathname === '/team-settings';
    }
    if (path === '/quarantine') {
      return location.pathname === '/quarantine';
    }
    if (path === '/proof') {
      return location.pathname === '/proof';
    }
    if (path === '/admin/users') {
      return location.pathname === '/admin/users';
    }
    return location.pathname.startsWith(path);
  };

  function handleNavClick(path) {
    if (isMobile && onClose) onClose();
    navigate(path);
  }

  function currentRole() {
    if (isUserAdmin) return 'ADMIN';
    if (isOwner) return 'OWNER';
    return 'MEMBER';
  }

  const showTextMessaging =
    (isOwner || isUserAdmin) && (SMS_ONBOARDING_LAUNCHED || isUserAdmin);

  const sidebarStyle = {
    width: '232px',
    background: 'var(--surface)',
    color: 'var(--ink)',
    position: 'fixed',
    left: 0,
    top: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid var(--hairline-3)',
    zIndex: 1001,
    transition: 'transform 0.25s ease-out',
    ...(isMobile ? { transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)' } : {}),
  };

  return (
    <>
      {isMobile && mobileOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          onClick={onClose}
          onKeyDown={(e) => e.key === 'Enter' && onClose?.()}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
          }}
        />
      )}

      <div className="console-sidebar" style={sidebarStyle}>

        {/* Mobile close button */}
        {isMobile && (
          <div style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid var(--hairline-2)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              style={{
                padding: '0.5rem',
                background: 'transparent',
                color: 'var(--dim)',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.2rem',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Wordmark + workspace menu */}
        <div style={{
          padding: '20px 20px 16px',
          borderBottom: '1px solid var(--hairline-2)',
        }}>
          <WorkspaceMenu />
        </div>

        {/* Navigation + footer share one scrollport so Log out is reachable
            on iOS (100vh extends below the visible toolbar). */}
        <div className="console-sidebar-scroll">
        <nav style={{
          flex: 1,
          padding: '14px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}>
          {/* OPERATE */}
          <div className="console-nav-section-label" style={{ paddingTop: '8px' }}>OPERATE</div>

          <button
            className={`console-nav-item${isActive('/dashboard') ? ' active' : ''}`}
            onClick={() => handleNavClick('/dashboard')}
          >
            Today
          </button>

          {canAccessLeadsPage && (
            <button
              className={`console-nav-item${isActive('/leads') ? ' active' : ''}`}
              onClick={() => handleNavClick('/leads')}
            >
              Inbox
            </button>
          )}

          {canAccessLeadsPage && (
            <button
              className={`console-nav-item${isActive('/pipeline') ? ' active' : ''}`}
              onClick={() => handleNavClick('/pipeline')}
            >
              Pipeline
            </button>
          )}

          <button
            className={`console-nav-item${isActive('/quarantine') ? ' active' : ''}`}
            onClick={() => handleNavClick('/quarantine')}
          >
            Quarantine
          </button>

          {canAccessLeadsPage && (
            <button
              className={`console-nav-item${isActive('/proof') ? ' active' : ''}`}
              onClick={() => handleNavClick('/proof')}
            >
              Proof
            </button>
          )}

          {/* BUILD */}
          <div className="console-nav-section-label">BUILD</div>

          <button
            className={`console-nav-item${isActive('/knowledge-base') ? ' active' : ''}`}
            onClick={() => handleNavClick('/knowledge-base')}
          >
            Knowledge Base
          </button>

          <button
            className={`console-nav-item${isActive('/form-builder') ? ' active' : ''}`}
            onClick={() => handleNavClick('/form-builder')}
          >
            Forms
          </button>

          <button
            className={`console-nav-item${isActive('/sites') ? ' active' : ''}`}
            onClick={() => handleNavClick('/sites')}
          >
            Sites
          </button>

          <button
            className={`console-nav-item${isActive('/chat') ? ' active' : ''}`}
            onClick={() => handleNavClick('/chat')}
          >
            Chat widget
          </button>

          <button
            className={`console-nav-item${isActive('/custom-domains') ? ' active' : ''}`}
            onClick={() => handleNavClick('/custom-domains')}
          >
            Domains
          </button>

          {/* ACCOUNT */}
          <div className="console-nav-section-label">ACCOUNT</div>

          <button
            className={`console-nav-item${isActive('/advanced-settings') ? ' active' : ''}`}
            onClick={() => handleNavClick('/advanced-settings')}
          >
            Team &amp; integrations
          </button>

          <button
            className={`console-nav-item${isActive('/billing') ? ' active' : ''}`}
            onClick={() => handleNavClick('/billing')}
          >
            Billing
          </button>

          {showTextMessaging && (
            <button
              className={`console-nav-item${isActive('/text-messaging') ? ' active' : ''}`}
              onClick={() => handleNavClick('/text-messaging')}
            >
              Text Messaging
            </button>
          )}

          <button
            className={`console-nav-item${isActive('/profile') ? ' active' : ''}`}
            onClick={() => handleNavClick('/profile')}
          >
            Profile
          </button>

          {isUserAdmin && (
            <button
              className={`console-nav-item${isActive('/admin/users') ? ' active' : ''}`}
              onClick={() => handleNavClick('/admin/users')}
            >
              User management
            </button>
          )}
        </nav>

        {/* Footer: email + role + logout */}
        <div
          className="console-sidebar-footer"
          style={{
          borderTop: '1px solid var(--hairline-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'var(--ink)',
            }}>
              {currentUser?.email || ''}
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '10px',
              color: 'var(--faint)',
              marginTop: '2px',
              letterSpacing: '0.06em',
            }}>
              {currentRole()}
            </div>
          </div>
          <button
            type="button"
            className="console-logout-btn"
            onClick={logout}
            aria-label="Log out"
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '10px',
              color: 'var(--dim)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              letterSpacing: '0.04em',
              padding: '4px 0',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--danger)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dim)'; }}
          >
            EXIT →
          </button>
        </div>
        </div>

      </div>
    </>
  );
}
